import { useEffect } from 'react';
import { default as React } from 'react';
import useTimeoutFn from 'react-use/lib/useTimeoutFn';

import {
  FgaV1ResourceRelation,
  getTypeOrNull,
  useCheckResourcePermissionsMutation,
} from '@spotify/confidence-graphql';

// We keep a cache of permissions for each resource. Preferrably we
// could check permission using a query, and cache the result. But
// we can't do that, so we use our own cache and provide a
// "cache-and-network" behavior to the users of this hook.
export const RESOURCE_PERMISSION_CACHE = new Map<string, boolean>();

export type UseCheckResourcePermissionsOptions = {
  // Name of the resource to check permissions for
  name?: string;

  // Preloaded permissions for the resource. If provided, we will use
  resourceRelations?: Array<Pick<FgaV1ResourceRelation, 'relation'>> | null;

  // The relation. e.g., 'share'
  can?: string;

  // Optimistic permissions. If true, we will assume that the user has
  // permissions to perform the action. If false, we will assume that
  // the user does not have permissions to perform the action.
  optimistic?: boolean;
};

const cacheKey = (name: string, can: string) => `${name}:${can}`;

export function useCheckResourcePermissions({
  can,
  name,
  resourceRelations,
  optimistic = false,
}: UseCheckResourcePermissionsOptions = {}): {
  allowed: boolean;
  hasData: boolean;
  error: Error | undefined;
  loading: boolean;
} {
  const [check, { data, error, loading }] =
    useCheckResourcePermissionsMutation();

  // Fall back to optimistic value if we don't have a name or
  // if nothing is in the cache.
  const cached =
    name && can && RESOURCE_PERMISSION_CACHE.has(cacheKey(name, can))
      ? RESOURCE_PERMISSION_CACHE.get(cacheKey(name, can))!
      : optimistic;

  // TODO: This is a workaround for a bug where Apollo doesn't properly
  // make the request when the hook is first mounted. It seems to
  // somehow be related to the event loop, and could only be reproduced
  // in the Admin plugin subpages on first page load (not when navigating
  // between different pages).

  const [isReady, cancel, reset] = useTimeoutFn(() => {
    if (name && can && !resourceRelations) {
      check({
        variables: {
          resourceRelations: [
            {
              resource: name,
              relation: `can_${can}`,
            },
          ],
        },
      });
    }
  }, 500);

  React.useEffect(() => {
    if (!name || !can) {
      cancel();
    } else {
      reset();
    }
  }, [name, can, cancel, reset]);

  // If we have preloaded permissions, we can use them to determine if
  // the user has permissions.
  const preloadedAllowed = resourceRelations?.some(
    r => r.relation === `can_${can}`,
  );

  const allowed =
    preloadedAllowed ?? getTypeOrNull(data?.executeCheck)?.allAllowed ?? cached;

  useEffect(() => {
    if (name && can && !preloadedAllowed) {
      // Remember the result of the permission check for the next
      // time we check the same resource.
      RESOURCE_PERMISSION_CACHE.set(cacheKey(name, can), allowed);
    }
  }, [allowed, name, can, preloadedAllowed]);

  return {
    allowed,
    hasData: !!data,
    error,
    loading: loading || !isReady(),
  };
}
